import { Modal } from 'antd';
import Box from 'components/Box';
import Text from 'components/Text';
import Button from 'components/Button';
import { PAYMENT_TYPE } from 'features/payment/services/constants';
import reportSelectors from 'features/report/services/selectors';
import reportApis from 'features/report/services/apis';
import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import shopSelectors from 'services/shop/selectors';
import { formatCurrency, formatNumber } from 'utils/formatCurrency';
import { capitalize, find } from 'lodash';
import { AxiosResponse } from 'axios';
import { ReportType } from '../../constants';
import { renderTime, formatShopInfo } from '../../utils';
import { minusValue } from '../../SalonReport/EmployeeTable/calc';
import { IPrintSummarySalon } from 'features/report/services/types/reportStaff';
import styled from 'styled-components';

export type PrintSummaryBillRef = {
  handlePrint: () => void;
};

export type PrintSummaryBillProps = {};

const PrintSummaryBill = forwardRef<PrintSummaryBillRef, PrintSummaryBillProps>((props, ref) => {
  const divRef = useRef<HTMLDivElement>(null);
  const dataAppointmentRef = useRef(reportSelectors.getParams()?.[ReportType.REVENUE]);
  const printTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideModalTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const [modalVisible, setModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dataSummarySalon, setDataSummarySalon] = useState<IPrintSummarySalon | null>(null);
  const dataAppointment = reportSelectors.getParams()?.[ReportType.REVENUE];
  const shopInfo = shopSelectors.data.shopInfo();
  const shopInfoFormatted = formatShopInfo(shopInfo);
  const payments = shopSelectors.data.orderPaymentMethods();
  const giftCreditLabel = useMemo(
    () => find(payments, (o) => o.methodType === PAYMENT_TYPE.GIFT_CREDIT)?.name || 'Gift Credit',
    [payments]
  );

  React.useEffect(() => {
    dataAppointmentRef.current = dataAppointment;
    setDataSummarySalon(null);
    setModalVisible(false);
    setIsLoading(false);
  }, [dataAppointment]);

  React.useEffect(() => {
    return () => {
      if (printTimeoutRef.current) {
        clearTimeout(printTimeoutRef.current);
      }
      if (hideModalTimeoutRef.current) {
        clearTimeout(hideModalTimeoutRef.current);
      }
    };
  }, []);

  const crossSaleList = useMemo(() => [
    { name: 'Services', price: dataSummarySalon?.saleItem?.totalSaleService },
    { name: 'Total Services', price: dataSummarySalon?.totalServices, number: true },
    { name: 'Products', price: dataSummarySalon?.saleItem?.totalSaleProduct },
    { name: 'Sell Gift Cards', price: dataSummarySalon?.saleItem?.totalSaleGiftCard },
    { name: 'VIP Programs', price: dataSummarySalon?.saleItem?.totalSaleVIP },
    { name: 'Sale Taxes', price: dataSummarySalon?.feeItem?.saleTax },
    { name: 'Use Taxes', price: dataSummarySalon?.feeItem?.useTax },
    { name: 'Supply Fees', price: dataSummarySalon?.supplyFee },
  ], [dataSummarySalon]);

  const lostList = useMemo(() => [
    { name: 'Staff Discounts', price: dataSummarySalon?.staffDiscount },
    { name: 'Refunds', price: dataSummarySalon?.totalRefund },
    { name: 'Voids', price: dataSummarySalon?.totalVoid },
    { name: 'Redeemed Gift Cards', price: dataSummarySalon?.paymentItem?.totalGiftCard },
    { name: 'Redeemed Loyalty Points ', price: dataSummarySalon?.paymentItem?.totalRedeemPoint },
  ], [dataSummarySalon]);

  const staticList = useMemo(() => [
    { name: 'Number Of Active Tickets', price: dataSummarySalon?.statistics?.numberTicket },
    { name: 'Number Of Voided Tickets', price: dataSummarySalon?.statistics?.numberTicketVoid },
    { name: 'Average Ticket', price: formatCurrency(dataSummarySalon?.statistics?.averageAmount) },
    { name: 'Return Customers', price: dataSummarySalon?.statistics?.returnCustomer },
    { name: 'Walk - In Customers', price: dataSummarySalon?.statistics?.totalWalkIn },
  ], [dataSummarySalon]);

  const onPrintSummary = useReactToPrint({
    content: () => divRef.current,
    copyStyles: true,
  });

  React.useEffect(() => {
    if (modalVisible && dataSummarySalon && Object.keys(dataSummarySalon).length > 0) {
      if (!divRef.current) return;
      
      if (printTimeoutRef.current) {
        clearTimeout(printTimeoutRef.current);
      }
      if (hideModalTimeoutRef.current) {
        clearTimeout(hideModalTimeoutRef.current);
      }
      
      hideModalTimeoutRef.current = setTimeout(() => {
        onPrintSummary();
        
        setTimeout(() => {
          setIsLoading(false);
          setModalVisible(false);
        }, 200);
      }, 800);
    }
    
    return () => {
      if (printTimeoutRef.current) {
        clearTimeout(printTimeoutRef.current);
      }
      if (hideModalTimeoutRef.current) {
        clearTimeout(hideModalTimeoutRef.current);
      }
    };
  }, [dataSummarySalon, modalVisible, onPrintSummary]);

  const handlePrint = async () => {
    const dataAppointment = dataAppointmentRef.current;
    if (!dataAppointment || isLoading) return;

    setDataSummarySalon(null);
    setIsLoading(true);
    setModalVisible(true);

    try {
      const response: AxiosResponse<{ data: IPrintSummarySalon }> = await reportApis.getPrintSummarySalon({
        startDate: dataAppointment.startTime,
        endDate: dataAppointment.endTime
      });
      
      if (response?.data?.data) {
        setDataSummarySalon(response.data.data);
      } else {
        setIsLoading(false);
        setModalVisible(false);
      }
    } catch (error) {
      setIsLoading(false);
      setModalVisible(false);
    }
  };

  useImperativeHandle(ref, () => ({
    handlePrint,
  }));

  return (
    <>
      <Button 
        width='9rem' 
        cardCashier 
        ntype='SECONDARY' 
        className='btn-print' 
        icon="print" 
        onClick={handlePrint}
        loading={isLoading}
        disabled={isLoading}
      >
        Print Summary
      </Button>
    <CompactModal
      visible={modalVisible}
      footer={null}
      closable={false}
      mask={false}
      width="auto"
      style={{ top: '-9999px', left: '-9999px', position: 'fixed' }}
      bodyStyle={{ padding: 0, margin: 0 }}
      getContainer={false}
    >
      {dataSummarySalon && (
        <div ref={divRef}>
          <Box className="center" overflow="auto">
            <TicketBox>
          <LogoBox>
            <ShopName>{shopInfoFormatted.name}</ShopName>
            <ShopAddress>{shopInfoFormatted.streetAddress}</ShopAddress>
            <ShopAddress>{shopInfoFormatted.cityStateZip}</ShopAddress>
            <ShopPhone>{shopInfoFormatted.phone}</ShopPhone>
          </LogoBox>
          <Box className="center">
            <Text printMode variant="CONTENT_2" color="text_2">
              {renderTime(dataAppointment)}
            </Text>
          </Box>
          <Box>
            <CrossSales>
              <Box className="space-between" bb="print_line" highlight>
                <Text printMode variant="CAPTION_2" color="text_3" className="title-highlight fs-lg">
                  Gross Sales
                </Text>
                <Text printMode variant="CAPTION_2" color="text_3" className="text-highlight fs-lg">
                  {formatCurrency(dataSummarySalon?.saleItem?.crossSales)}
                </Text>
              </Box>
              <Box px="1" display="flex" flexDirection="column" bb="print_line">
                {crossSaleList.map((item, index) => (
                  <Box key={index} className="space-between">
                    <Text printMode variant="CAPTION_2" color="text_3">
                      {item.name}
                    </Text>
                    <Text printMode variant="CAPTION_2" color="text_3">
                      {item.number ? formatNumber(item.price) : formatCurrency(item.price)}
                    </Text>
                  </Box>
                ))}
              </Box>
              <Text mb={0.5} />
              <Box display="flex" flexDirection="column">
                <Box px="1">
                  <Box className="space-between">
                    <Text printMode variant="CAPTION_2" color="text_3">
                      Credit Card
                    </Text>
                    <Text printMode variant="CAPTION_2" color="text_3">
                      {formatCurrency(dataSummarySalon?.paymentItem?.totalCreditCard)}
                    </Text>
                  </Box>
                  <Box bt="print_line" bb="print_line" bStyle="dotted">
                    <Box pl="3">
                      <Box className="space-between">
                        <Text printMode variant="CAPTION_2" color="text_3">
                          Credit Card Processing
                        </Text>
                        <Text printMode variant="CAPTION_2" color="text_3">
                          {formatCurrency(dataSummarySalon?.paymentItem?.saleCreditCard)}
                        </Text>
                      </Box>
                      <Box className="space-between">
                        <Text printMode variant="CAPTION_2" color="text_3">
                          Card Fees
                        </Text>
                        <Text printMode variant="CAPTION_2" color="text_3">
                          {formatCurrency(dataSummarySalon?.paymentItem?.feeCreditCard)}
                        </Text>
                      </Box>
                      <Box className="space-between">
                        <Text printMode variant="CAPTION_2" color="text_3">
                          Total Tips
                        </Text>
                        <Text printMode variant="CAPTION_2" color="text_3">
                          {formatCurrency(dataSummarySalon?.paymentItem?.tipCreditCard)}
                        </Text>
                      </Box>
                      <Box className="space-between">
                        <Text printMode variant="CAPTION_2" color="text_3">
                          Tip Fee
                        </Text>
                        <Text printMode variant="CAPTION_2" color="text_3">
                          {formatCurrency(dataSummarySalon?.totalTipFee)}
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                  <Box display="flex" flexDirection="column">
                    <Box className="space-between">
                      <Text printMode variant="CAPTION_2" color="text_3">
                        Cash
                      </Text>
                      <Text printMode variant="CAPTION_2" color="text_3">
                        {formatCurrency(dataSummarySalon?.paymentItem?.totalCash)}
                      </Text>
                    </Box>
                    <Box className="space-between">
                      <Text printMode variant="CAPTION_2" color="text_3">
                        Gift Card
                      </Text>
                      <Text printMode variant="CAPTION_2" color="text_3">
                        {formatCurrency(dataSummarySalon?.paymentItem?.totalGiftCard)}
                      </Text>
                    </Box>
                    <Box className="space-between">
                      <Text printMode variant="CAPTION_2" color="text_3">
                        Checks
                      </Text>
                      <Text printMode variant="CAPTION_2" color="text_3">
                        {formatCurrency(dataSummarySalon?.paymentItem?.totalCheck)}
                      </Text>
                    </Box>
                    <Box className="space-between">
                      <Text printMode variant="CAPTION_2" color="text_3" style={{ textTransform: 'capitalize' }}>
                        {capitalize(giftCreditLabel)}
                      </Text>
                      <Text printMode variant="CAPTION_2" color="text_3">
                        {formatCurrency(dataSummarySalon?.paymentItem?.totalGiftCredit)}
                      </Text>
                    </Box>
                    <Box className="space-between">
                      <Text printMode variant="CAPTION_2" color="text_3">
                        Loyalty Point
                      </Text>
                      <Text printMode variant="CAPTION_2" color="text_3">
                        {formatCurrency(dataSummarySalon?.paymentItem?.totalRedeemPoint)}
                      </Text>
                    </Box>
                    <Box display="flex" flexDirection="column">
                      <Box className="space-between">
                        <Text printMode variant="CAPTION_2" color="text_3">
                          Other(s)
                        </Text>
                        <Text printMode variant="CAPTION_2" color="text_3">
                          {formatCurrency(dataSummarySalon?.paymentItem?.totalOther)}
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CrossSales>
            <Text mb={0.5} />

            <Box>
              <Box px="1">
                <Text mb={0.5} />
                <Box display="flex" flexDirection="column">
                  <Box className="space-between" highlight>
                    <Text printMode variant="CAPTION_2" color="text_3" className="title-highlight fs-lg">
                      Deductions
                    </Text>
                    <Text printMode variant="CAPTION_2" color="text_3" className="text-highlight fs-lg">
                      {minusValue(dataSummarySalon?.deductions || 0)}
                    </Text>
                  </Box>
                  <Box className="space-between">
                    <Text printMode variant="CAPTION_2" color="text_3">
                      Company Discounts
                    </Text>
                    <Text printMode variant="CAPTION_2" color="text_3">
                      {formatCurrency(dataSummarySalon?.ownerDiscount)}
                    </Text>
                  </Box>
                  <Box bt="print_line" bb="print_line" bStyle="dotted">
                    <Box pl="3">
                      <Box className="space-between">
                        <Text printMode variant="CAPTION_2" color="text_3">
                          Manual
                        </Text>
                        <Text printMode variant="CAPTION_2" color="text_3">
                          {formatCurrency(dataSummarySalon?.discountManual)}
                        </Text>
                      </Box>
                      <Box className="space-between">
                        <Text printMode variant="CAPTION_2" color="text_3">
                          Coupons
                        </Text>
                        <Text printMode variant="CAPTION_2" color="text_3">
                          {formatCurrency(dataSummarySalon?.discountCoupon)}
                        </Text>
                      </Box>
                      <Box className="space-between">
                        <Text printMode variant="CAPTION_2" color="text_3">
                          Cash Incentives
                        </Text>
                        <Text printMode variant="CAPTION_2" color="text_3">
                          {formatCurrency(dataSummarySalon?.discountCashIncentive)}
                        </Text>
                      </Box>
                    </Box>
                  </Box>

                  {lostList.map((item, index) => (
                    <Box key={index} className="space-between">
                      <Text printMode variant="CAPTION_2" color="text_3">
                        {item.name}
                      </Text>
                      <Text printMode variant="CAPTION_2" color="text_3">
                        {formatCurrency(item.price)}
                      </Text>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
            <Text mb={0.5} />
            <Box px="1">
              <Box className="space-between" highlight bt="print_line" bb="print_line">
                <Text printMode variant="CAPTION_2" color="text_3" className="text-highlight title-highlight fs-lg">
                  Total Net Sale
                </Text>
                <Text printMode variant="CAPTION_2" color="text_3" className="title-highlight fs-lg">
                  {formatCurrency(dataSummarySalon?.netSales)}
                </Text>
              </Box>
            </Box>

            <Text mb={1} />
            <Box>
              <Box px="1">
                <Box bb="print_line" highlight>
                  <Text printMode variant="CAPTION_2" color="text_3" className="title-highlight">
                    Statistics
                  </Text>
                </Box>
                <Text mb={0.5} />
                <Box display="flex" flexDirection="column">
                  {staticList.map((item, index) => (
                    <Box key={index} className="space-between">
                      <Text printMode variant="CAPTION_2" color="text_3">
                        {item.name}
                      </Text>
                      <Text printMode variant="CAPTION_2" color="text_3">
                        {item.price}
                      </Text>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
            </TicketBox>
          </Box>
        </div>
      )}
    </CompactModal>
    </>
  );
});

PrintSummaryBill.displayName = 'PrintSummaryBill';

export default PrintSummaryBill;

const TicketBox = styled.div`
  border-radius: 20px;
  background: #fff;
  width: 19.5rem;
  height: fit-content;
  padding: 1.5rem 0 2rem;
`;

const LogoBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1px;
  text-align: center;
`;

const ShopName = styled.div`
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 2px;
  color: black;
`;

const ShopAddress = styled.div`
  font-size: 14px;
  margin-bottom: 2px;
  color: black;
`;

const ShopPhone = styled.div`
  font-size: 14px;
  margin-bottom: 2px;
  color: black;
`;

const CrossSales = styled.div`
  padding: 5px 4px;
`;

const CompactModal = styled(Modal)`
  .ant-modal {
    margin: 0 !important;
    padding: 0 !important;
    max-width: none !important;
  }
  .ant-modal-content {
    padding: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
  }
  .ant-modal-body {
    padding: 0 !important;
  }
  .ant-modal-mask {
    display: none !important;
  }
`;

